'use no memo';

import type { ColumnDef, ColumnFiltersState, OnChangeFn, Row, RowSelectionState, SortingState, Table, VisibilityState } from '@tanstack/react-table';

import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useState } from 'react';

import { useLocalStorage } from '@/hooks/local-storage';

export interface UseTanstackTableProps<TData> {
  name: string;
  columns: ColumnDef<TData>[];
  data: TData[];
  defaultColumnVisibility?: VisibilityState;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string;
}

export interface UseTanstackTableReturn<TData> {
  table: Table<TData>;
  /** Set global filter - use this for search input onChange */
  setGlobalFilter: (value: string) => void;
}

export function useTanstackTable<TData>({
  name,
  columns,
  data,
  getRowId,
  defaultColumnVisibility = {},
  rowSelection: externalRowSelection,
  onRowSelectionChange: externalOnRowSelectionChange,
}: UseTanstackTableProps<TData>): UseTanstackTableReturn<TData> {
  // Local state
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});

  // Persisted state (survives page refresh)
  const [columnFilters, setColumnFilters] = useLocalStorage<ColumnFiltersState>(`tanstackTable-${name}-columnFilters`, []);
  const [columnVisibility, setColumnVisibility] = useLocalStorage<VisibilityState>(`tanstackTable-${name}-columnVisibility`, defaultColumnVisibility);

  // Use external row selection if provided, otherwise use internal
  const rowSelection = externalRowSelection ?? internalRowSelection;
  const setRowSelection = externalOnRowSelectionChange ?? setInternalRowSelection;

  // Initialize table with TanStack Table's reactive hooks
  const table = useReactTable<TData>({
    data,
    columns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Row selection
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    // Column visibility
    onColumnVisibilityChange: setColumnVisibility,
    // Global filter
    enableFilters: true,
    onGlobalFilterChange: setGlobalFilter,
    // Column filters
    onColumnFiltersChange: setColumnFilters,
    // Sorting
    onSortingChange: setSorting,
    // Controlled state
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      globalFilter,
      columnFilters,
    },
  });

  return {
    table,
    setGlobalFilter,
  };
}
